import React from 'react';
import { Link } from 'react-router-dom';
import './DeleteAccount.css';

/**
 * Public account-deletion instructions at `/delete-account` — no sign-in required.
 *
 * WHY A PUBLIC PAGE AT ALL. Deleting an account already works in both apps
 * (Settings → Delete account), but Google Play's Data safety form asks for a URL
 * where a user can request deletion, and requires that the URL be reachable
 * WITHOUT signing in. Someone who has lost access to their account, or deleted
 * the app before deleting the account, cannot use an in-app button — so this
 * page states what deletion removes, what survives it and for how long, and
 * gives a route that does not depend on being able to sign in.
 *
 * Every retention figure here is copied from the privacy policy rather than
 * restated, so the two cannot drift: when a retention period changes, change it
 * in PrivacyPolicy.tsx and here in the same PR, and bump LAST_UPDATED.
 *
 * Beta draft: not yet reviewed by a lawyer (LAUNCH_CHECKLIST.md §2).
 */

const LAST_UPDATED = 'October 31, 2026';
const PRIVACY_EMAIL = 'privacy@wayplume.com';

const DeleteAccount: React.FC = () => (
  <div className="delete-account-page">
    <header className="delete-account-page__nav">
      <Link className="delete-account-page__brand" to="/">
        <span className="delete-account-page__mark">W</span>
        <span>Wayplume</span>
      </Link>
    </header>

    <main className="delete-account-page__body">
      <h1>Delete your Wayplume account</h1>
      <p className="delete-account-page__meta">
        Last updated: {LAST_UPDATED} · Applies to the Wayplume website and the Wayplume apps for iOS and
        Android.
      </p>

      <p>
        You can delete your Wayplume account and its data at any time. Deletion is permanent — we cannot
        restore an account once it has been deleted, so please export anything you want to keep first.
      </p>

      <h2>Delete it yourself, in the app or on the web</h2>
      <p>This is the fastest route and takes effect immediately.</p>
      <ol>
        {/* Linked relatively, not to a domain: the beta is served from
            beta.cookies-home-playground.com, so a hard-coded "wayplume.com"
            would be a link to nowhere. */}
        <li>
          Open Wayplume on your phone, or <Link to="/">sign in on the web</Link>.
        </li>
        <li>
          Go to <strong>Settings</strong> → <strong>Delete account</strong>.
        </li>
        <li>
          Type <strong>DELETE</strong> to confirm. Your account is removed straight away and you are signed
          out on every device.
        </li>
      </ol>

      <h2>Ask us to delete it for you</h2>
      <p>
        If you cannot sign in — a lost password you cannot reset, or an email address you no longer have
        access to — email <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a> from the address on the
        account, or tell us the email address and the display name you used. We complete deletion requests
        within 30 days and will confirm by email when it is done. We may ask you to confirm the request
        from the account's email address first, so that nobody else can delete your account.
      </p>

      <h2>What deletion removes</h2>
      <p>Deleting your account permanently removes:</p>
      <ul>
        <li>your profile, including your name, handle, photo, date of birth and privacy settings;</li>
        <li>your trips, their activities, budgets, checklists and any photos attached to them;</li>
        <li>your posts, comments, likes and bookmarks;</li>
        <li>your direct messages and both inboxes;</li>
        <li>who you follow and who follows you, in both directions;</li>
        <li>your conversations with the AI planner, including anything it remembered about you;</li>
        <li>your notifications and push notification tokens;</li>
        <li>your uploaded photos, as files, from our storage; and</li>
        <li>your sign-in identity, so the email address can be used to sign up again.</li>
      </ul>

      <h2>What does not disappear, and why</h2>
      <ul>
        <li>
          <strong>Messages you sent in a group conversation</strong> are replaced with a "deleted"
          placeholder rather than removed, so the remaining participants' conversation still makes sense.
        </li>
        <li>
          <strong>Trips somebody else copied</strong> before you deleted yours stay with them — a copy is
          their own trip, not a view of yours.
        </li>
        <li>
          <strong>Encrypted database backups</strong> may still contain deleted information for up to 35
          days, after which they are overwritten.
        </li>
        <li>
          <strong>Server logs</strong> are kept for up to 90 days and then deleted.
        </li>
      </ul>

      <p>
        For the full picture of what we collect and how long we keep it, see our{' '}
        <Link to="/privacy">Privacy Policy</Link>. If anything here is unclear, email{' '}
        <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>.
      </p>
    </main>
  </div>
);

export default DeleteAccount;
